import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { Orientation, PaperSize } from './print-options'

interface Props {
  pages: React.ReactNode[]
  paper: PaperSize
  orientation: Orientation
  onDone: () => void
}

/** `afterprint` is not guaranteed to fire (some browsers, some cancel paths); without a fallback the
 * dialog would stay stuck in its printing state forever. */
const AFTERPRINT_FALLBACK_MS = 4000

/**
 * Browser printing (spec C §5): every page is mounted into `document.body` under
 * `#schedule-print-root`, the rest of the app is hidden by the `@media print` rules in
 * `globals.css`, and `@page` is sized to the chosen paper so the browser does not scale the
 * SVG pages. `window.print()` waits for the Roboto face — printing before `document.fonts`
 * settles yields fallback metrics and clipped labels.
 *
 * The injected stylesheet also undoes the Radix modal's body scroll lock for print. The dialog that
 * mounts this portal is still open, so `react-remove-scroll-bar` has `body[data-scroll-locked]`
 * pinned to `overflow: hidden` (the classic cause of "only the first page prints") plus a
 * `margin-right` scrollbar gap that would narrow the page box under `@page { margin: 0 }`. Source
 * order beats that rule at equal specificity, so it belongs here rather than in `globals.css`.
 */
const PrintPortal: React.FC<Props> = ({ pages, paper, orientation, onDone }) => {
  const onDoneRef = useRef(onDone)
  useEffect(() => { onDoneRef.current = onDone })

  useEffect(() => {
    let disposed = false
    let fallback: ReturnType<typeof setTimeout> | undefined
    const done = () => {
      if (fallback) clearTimeout(fallback)
      onDoneRef.current()
    }

    document.body.classList.add('schedule-printing')
    const style = document.createElement('style')
    style.id = 'schedule-print-page'
    style.textContent = [
      `@page { size: ${paper} ${orientation}; margin: 0 }`,
      '@media print { body.schedule-printing { overflow: visible !important; position: static !important; margin: 0 !important; padding: 0 !important; } }',
    ].join('\n')
    document.head.appendChild(style)
    window.addEventListener('afterprint', done, { once: true })

    const fonts: any = (document as any).fonts
    const ready: Promise<any> = fonts && fonts.ready ? fonts.ready : Promise.resolve()
    const print = () => {
      if (disposed) return
      requestAnimationFrame(() => {
        if (disposed) return
        window.print()
        fallback = setTimeout(() => { if (!disposed) onDoneRef.current() }, AFTERPRINT_FALLBACK_MS)
      })
    }
    // A rejected `fonts.ready` must not swallow the print — fall back to the system face.
    ready.then(print, print)

    return () => {
      disposed = true
      if (fallback) clearTimeout(fallback)
      document.body.classList.remove('schedule-printing')
      if (style.parentNode) style.parentNode.removeChild(style)
      window.removeEventListener('afterprint', done)
    }
  }, [paper, orientation])

  return createPortal(<div id="schedule-print-root" className="schedule-print">{pages}</div>, document.body)
}

export default PrintPortal
